import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components/Icon';
import { SignalSheet, type SheetSeed } from '@/features/signals/SignalSheet';
import { SignalHistory } from '@/features/feed/SignalHistory';
import { deleteSignal, fetchCorpusStats, fetchFollowedTowns, fetchSignals } from '@/lib/api';
import { sayCountries, sayTowns } from '@/lib/corpus';
import type { Signal } from '@/lib/signals';
import './Signals.css';

/**
 * Manage signals — the saved-definition CRUD list.
 *
 * Reached from the Signals surface, not from the nav. A Signal is one saved definition
 * driving three outputs: this feed, the Legislative History rollup, and delivery. Editing one
 * reopens the same sheet used to create it, prefilled.
 *
 * Deleting is the one destructive action on the surface, so it confirms inline rather than
 * through a dialog — a Signal can carry months of match history behind its count badge.
 */
export function Signals() {
  const queryClient = useQueryClient();
  const [sheet, setSheet] = useState<SheetSeed | null>(null);
  const [historyFor, setHistoryFor] = useState<Signal | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const signalsQuery = useQuery({ queryKey: ['signals'], queryFn: fetchSignals });
  const townsQuery = useQuery({ queryKey: ['towns'], queryFn: fetchFollowedTowns });
  const statsQuery = useQuery({ queryKey: ['corpus'], queryFn: fetchCorpusStats });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSignal(id),
    onSuccess: () => {
      setConfirmDelete(null);
      void queryClient.invalidateQueries({ queryKey: ['signals'] });
      void queryClient.invalidateQueries({ queryKey: ['signal-feed'] });
    },
  });

  const signals = signalsQuery.data ?? [];
  const stats = statsQuery.data;
  const corpusSummary = stats
    ? `${sayTowns(stats)} towns · ${sayCountries(stats)} countries`
    : 'every place';

  return (
    <div className="signals">
      <header className="signals__head">
        <div>
          <h1 className="signals__title">Manage signals</h1>
          <p className="signals__subtitle">
            Each one watches for what you describe, fills your feed, and can alert you.
          </p>
        </div>
        <button type="button" className="signals__new" onClick={() => setSheet({})}>
          <Icon name="plus" /> New signal
        </button>
      </header>

      {signalsQuery.isLoading && <p className="signals__empty">Loading…</p>}

      {!signalsQuery.isLoading && signals.length === 0 && (
        <div className="signals__empty">
          <p>No signals yet.</p>
          <p className="signals__emptyHint">
            A signal is a standing description of what you care about — “battery storage
            moratorium” across New Jersey, say. It fills your feed as new meetings are
            processed.
          </p>
        </div>
      )}

      <ul className="signals__list">
        {signals.map((signal) => (
          <li key={signal.id} className="signals__row">
            <div className="signals__main">
              <span className="signals__name">
                <Icon name="signals" /> {signal.name}
              </span>
              <span className="signals__terms">{signal.terms}</span>
              <span className="signals__meta">
                {signal.meta} ·{' '}
                {{ realtime: 'Real-time', daily: 'Daily digest', weekly: 'Weekly' }[
                  signal.frequency
                ]}{' '}
                · {[signal.email && 'email', signal.whatsApp && 'WhatsApp']
                  .filter(Boolean)
                  .join(' + ') || 'no alerts'}
              </span>
            </div>

            <button
              type="button"
              className="signals__count"
              onClick={() => setHistoryFor(signal)}
            >
              {signal.total} <span>matches</span>
            </button>

            <div className="signals__actions">
              <button
                type="button"
                className="signals__action"
                onClick={() =>
                  setSheet({
                    id: signal.id,
                    name: signal.name,
                    terms: signal.terms,
                    scope: signal.scope,
                    bodies: signal.bodies,
                    categories: signal.categories,
                    frequency: signal.frequency,
                    email: signal.email,
                    whatsApp: signal.whatsApp,
                  })
                }
              >
                Edit
              </button>

              {confirmDelete === signal.id ? (
                <span className="signals__confirm">
                  <button
                    type="button"
                    className="signals__action signals__action--danger"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(signal.id)}
                  >
                    {remove.isPending ? 'Deleting…' : 'Delete for good'}
                  </button>
                  <button
                    type="button"
                    className="signals__action"
                    onClick={() => setConfirmDelete(null)}
                  >
                    Keep
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="signals__action"
                  onClick={() => setConfirmDelete(signal.id)}
                >
                  Delete
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {sheet && (
        <SignalSheet
          seed={sheet}
          followedTownCount={(townsQuery.data ?? []).length}
          corpusSummary={corpusSummary}
          onClose={() => setSheet(null)}
        />
      )}

      {historyFor && (
        <SignalHistory
          signalName={historyFor.name}
          total={historyFor.total}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}
